import {
  IAdvisorComparisonTableBlock,
  IAdvisorPackageCardBlock,
  IAdvisorPackageLineItem,
} from "@types";

import { ADVISOR_LINE_ITEM } from "../advisorContract";
import { Badge } from "../Badge";
import { cn } from "../cn";
import { ROW_STYLES, SENTIMENT_BADGE_VARIANT } from "../sentiment";
import { SURFACE } from "../theme";
import {
  alignFromNumeric,
  DataTable,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from "./DataTable";
import { HollowSeriesDot, SERIES, SeriesDot } from "./series";
import { DASH, numericColumns, parseAmount } from "./tableUtils";

// The backend also bakes a sentiment emoji into the sentence — stripped here since the badge
// colour already carries that signal.
const SENTIMENT_EMOJI_PREFIX = /^[\u{1F534}\u{1F7E2}⚪]\s*/u;

/**
 * Splits a "This offer is" sentence into its badge-worthy pieces: magnitude + money gap
 * normally ("60.9% higher", "+730,310 PKR"), just the magnitude when there's no gap to report,
 * or a single dash badge when there's no delta at all.
 */
function comparisonBadges(cell: string | undefined): string[] {
  const stripped = (cell ?? "").replace(SENTIMENT_EMOJI_PREFIX, "").trim();
  if (!stripped) return [DASH];
  const withGap = stripped.match(/^(.*?)\s*\((.+)\)$/);
  return withGap ? [withGap[1].trim(), withGap[2].trim()] : [stripped];
}

/**
 * S4_RECOMMEND's package card and comparison tables, restyled to match the visual language the
 * other funnel nodes already use — S1A's per-component colour markers, S1B's row markers and
 * card chrome. Every value printed is still exactly what the backend sent; parsing a cell back
 * into a number is done only to size a bar, never to reformat or replace the printed text.
 */

// The three funded components that build up to the target total cash, mapped to the same
// SERIES index the bar and the line-item dots both read from — one source, so they can't drift.
const COMPONENT_SERIES: Record<string, number> = {
  [ADVISOR_LINE_ITEM.ANNUAL_BASE]: 0,
  [ADVISOR_LINE_ITEM.CASH_ALLOWANCES]: 1,
  [ADVISOR_LINE_ITEM.TARGET_BONUS]: 2,
};

/**
 * The base/allowances/bonus/discretionary build-up to target total cash, as proportional
 * segments. Discretionary gets a hatch instead of a solid fill — it's cash no configured plan
 * actually funds, and the bar should say that before the callout explains it.
 */
function CompositionBar({ items }: { items: IAdvisorPackageLineItem[] }) {
  const segments = [
    ...Object.keys(COMPONENT_SERIES),
    ADVISOR_LINE_ITEM.DISCRETIONARY_BONUS,
  ]
    .map(label => items.find(item => item.label === label))
    .filter((item): item is IAdvisorPackageLineItem => Boolean(item))
    .map(item => ({ label: item.label, amount: parseAmount(item.value) ?? 0 }))
    .filter(item => item.amount > 0);

  const total = segments.reduce((sum, segment) => sum + segment.amount, 0);
  if (total <= 0) return null;

  return (
    <div className="border-b border-gray-200 px-4 pb-3 pt-3.5">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        {segments.map(segment => {
          const isDiscretionary =
            segment.label === ADVISOR_LINE_ITEM.DISCRETIONARY_BONUS;
          const seriesIndex = COMPONENT_SERIES[segment.label];
          return (
            <div
              key={segment.label}
              title={segment.label}
              style={{
                width: `${(segment.amount / total) * 100}%`,
                backgroundColor: isDiscretionary
                  ? undefined
                  : SERIES[seriesIndex].stroke,
                backgroundImage: isDiscretionary
                  ? "repeating-linear-gradient(135deg, #f59e0b 0 4px, #fde68a 4px 8px)"
                  : undefined,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function LineRow({
  item,
  isFirst,
}: {
  item: IAdvisorPackageLineItem;
  isFirst: boolean;
}) {
  const seriesIndex = COMPONENT_SERIES[item.label];
  const isDiscretionary = item.label === ADVISOR_LINE_ITEM.DISCRETIONARY_BONUS;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-4 py-2.5",
        !isFirst && "border-t border-gray-200",
        item.emphasis && "bg-gray-100"
      )}
    >
      <span
        className={cn(
          "flex items-center gap-2.5 text-sm",
          item.emphasis ? "font-semibold text-gray-800" : "text-gray-500"
        )}
      >
        {seriesIndex !== undefined && <SeriesDot index={seriesIndex} />}
        {isDiscretionary && <HollowSeriesDot />}
        {item.label}
      </span>
      <span
        className={cn(
          "text-sm",
          item.emphasis ? "font-semibold text-gray-800" : "text-gray-800"
        )}
      >
        {item.value}
      </span>
    </div>
  );
}

/**
 * S4's package card: the same line items as before, plus a composition bar and colour-coded
 * markers on the components that build up to it. Subtotal/total rows (TGC, TTC, TDC) stay
 * undotted — they're sums, not components — and keep the existing bold/tinted emphasis.
 */
export function PackageCompositionCard({
  block,
}: {
  block: IAdvisorPackageCardBlock;
}) {
  return (
    <div className={cn(SURFACE, "overflow-hidden")}>
      <CompositionBar items={block.items} />
      {block.items.map((item, i) => (
        <LineRow key={item.label} item={item} isFirst={i === 0} />
      ))}
    </div>
  );
}

/**
 * S4's "How it compares" table: the backend's two-column sentence-style comparison — "Compared
 * to" / "This offer is" — with the right cell's magnitude and money-delta pulled into their own
 * sentiment-coloured badges instead of one long tinted sentence.
 */
export function S4ComparisonsTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  return (
    // The label column is prose, so this table wraps rather than scrolling sideways.
    <DataTable scroll={false}>
      <DataTableHead columns={block.columns.slice(0, 2)} />
      <tbody>
        {block.rows.map((row, i) => {
          const [subject, comparison] = row.cells;
          const sentiment = row.sentiment ?? "neutral";
          return (
            <DataTableRow key={i}>
              <DataTableCell wrap className="font-medium">
                {subject}
              </DataTableCell>
              <DataTableCell wrap>
                <div className="flex flex-wrap gap-1.5">
                  {comparisonBadges(comparison).map((piece, j) => (
                    <Badge key={j} variant={SENTIMENT_BADGE_VARIANT[sentiment]}>
                      {piece}
                    </Badge>
                  ))}
                </div>
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </tbody>
    </DataTable>
  );
}

/**
 * S4's bonus breakdown — the same rows as the generic table, with a row marker per component
 * (mirroring S1B's per-row treatment) and an amber left accent on the discretionary/unfunded
 * row, echoing the composition bar's hatch.
 */
export function BonusBreakdownTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  const numeric = numericColumns(block);
  const align = alignFromNumeric(numeric);

  return (
    <DataTable>
      <DataTableHead columns={block.columns} align={align} />
      <tbody>
        {block.rows.map((row, r) => {
          const discretionary = row.sentiment === "warning";
          return (
            <DataTableRow
              key={r}
              className={cn(discretionary && ROW_STYLES.warning)}
            >
              {row.cells.map((cell, c) => (
                <DataTableCell
                  key={c}
                  align={align[c]}
                  className={cn(
                    c === 0 && "font-medium",
                    c === 0 && discretionary && "border-l-2 border-l-amber-400",
                    c > 0 && !numeric[c] && "text-gray-500"
                  )}
                >
                  {c === 0 ? (
                    <span className="flex items-center gap-2.5">
                      <SeriesDot index={r} />
                      {cell}
                    </span>
                  ) : (
                    cell
                  )}
                </DataTableCell>
              ))}
            </DataTableRow>
          );
        })}
      </tbody>
    </DataTable>
  );
}
